var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import Button from "@mui/material/Button";
import { prepareZXingModule, readBarcodes } from "zxing-wasm/reader";
// Функция для парсинга JSON из QR кода
function parseAuthQrCode(input) {
    try {
        const data = JSON.parse(input);
        if (data.url &&
            data.code &&
            typeof data.url === "string" &&
            typeof data.code === "string") {
            return { url: data.url, code: data.code };
        }
        return null;
    }
    catch (_a) {
        return null;
    }
}
// Функция для нормализации URL (убирает trailing slash)
function normalizeUrl(url) {
    return url.replace(/\/$/, '');
}
const AuthQrCodeReader = ({ onAuthCodeFound, onClose, }) => {
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const [result, setResult] = useState("No result");
    const [error, setError] = useState(null);
    const [isScanning, setIsScanning] = useState(true);
    const [facingMode, setFacingMode] = useState("environment");
    const [showSuccess, setShowSuccess] = useState(false);
    const [showUrlMismatch, setShowUrlMismatch] = useState(false);
    const [qrData, setQrData] = useState(null);
    const streamRef = useRef(null);
    const rafRef = useRef(null);
    const frameSkipRef = useRef(0);
    const readyRef = useRef(false);
    const clearResult = () => {
        setResult("No result");
        setError(null);
        setIsScanning(true);
        setShowSuccess(false);
        setShowUrlMismatch(false);
        setQrData(null);
    };
    const toggleCamera = () => {
        setFacingMode((prev) => (prev === "environment" ? "user" : "environment"));
        setIsScanning(true);
        setResult("No result");
        setError(null);
        setShowSuccess(false);
        setShowUrlMismatch(false);
        setQrData(null);
    };
    const handleAuthCodeApply = (code) => {
        setIsScanning(false);
        stopAll();
        onAuthCodeFound === null || onAuthCodeFound === void 0 ? void 0 : onAuthCodeFound(code);
        setShowSuccess(true);
    };
    const calculateBrightness = (imageData) => {
        let total = 0;
        const data = imageData.data;
        for (let i = 0; i < data.length; i += 4) {
            total += (data[i] + data[i + 1] + data[i + 2]) / 3;
        }
        return total / (imageData.width * imageData.height);
    };
    const stopAll = () => {
        var _a;
        if (rafRef.current) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }
        (_a = streamRef.current) === null || _a === void 0 ? void 0 : _a.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
    };
    useEffect(() => {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (!video || !canvas)
            return;
        let brightnessTimer = null;
        const start = () => __awaiter(void 0, void 0, void 0, function* () {
            try {
                yield prepareZXingModule();
                stopAll();
                const constraints = {
                    video: {
                        facingMode,
                        width: { ideal: 1280 },
                        height: { ideal: 720 },
                        frameRate: { ideal: 30, max: 30 },
                    },
                    audio: false,
                };
                const stream = yield navigator.mediaDevices.getUserMedia(constraints);
                streamRef.current = stream;
                video.srcObject = stream;
                yield video.play();
                const ctx = canvas.getContext("2d");
                if (!ctx) {
                    setError("Не удалось инициализировать контекст canvas");
                    setIsScanning(false);
                    return;
                }
                const scanLoop = () => __awaiter(void 0, void 0, void 0, function* () {
                    if (!isScanning || !videoRef.current || !canvasRef.current)
                        return;
                    rafRef.current = requestAnimationFrame(scanLoop);
                    frameSkipRef.current = (frameSkipRef.current + 1) % 2;
                    if (frameSkipRef.current !== 0)
                        return;
                    const v = videoRef.current;
                    const c = canvasRef.current;
                    const targetW = 1280;
                    const scale = Math.min(1, targetW / (v.videoWidth || targetW));
                    const w = Math.floor((v.videoWidth || targetW) * scale);
                    const h = Math.floor((v.videoHeight || 720) * scale);
                    c.width = w;
                    c.height = h;
                    ctx.drawImage(v, 0, 0, w, h);
                    const imageData = ctx.getImageData(0, 0, w, h);
                    if (!readyRef.current) {
                        const b = calculateBrightness(imageData);
                        if (b < 35) {
                            setResult("Слишком темно, улучшите освещение");
                            return;
                        }
                        readyRef.current = true;
                    }
                    try {
                        const out = yield readBarcodes(imageData, {
                            tryHarder: true,
                            formats: ["QRCode"],
                            maxNumberOfSymbols: 1,
                        });
                        if (out.length > 0) {
                            const text = out[0].text;
                            const parsed = parseAuthQrCode(text);
                            if (parsed) {
                                setQrData(parsed);
                                setResult(text);
                                setIsScanning(false);
                                stopAll();
                                // Проверяем URL
                                const currentOrigin = normalizeUrl(window.location.origin);
                                const qrUrl = normalizeUrl(parsed.url);
                                if (qrUrl === currentOrigin) {
                                    // URL совпадает - сразу применяем код
                                    handleAuthCodeApply(parsed.code);
                                }
                                else {
                                    // URL не совпадает - показываем диалог подтверждения
                                    setShowUrlMismatch(true);
                                }
                            }
                            else {
                                setResult("QR-код не содержит данные авторизации");
                            }
                        }
                    }
                    catch (_a) {
                        // игнорируем ошибки кадра
                    }
                });
                brightnessTimer = window.setInterval(() => {
                    if (!videoRef.current || !canvasRef.current)
                        return;
                    if (video.readyState === video.HAVE_ENOUGH_DATA) {
                        const c = canvasRef.current;
                        const ctx2 = c.getContext("2d");
                        if (!ctx2)
                            return;
                        c.width = 320;
                        c.height =
                            Math.floor((320 / video.videoWidth) * video.videoHeight) || 180;
                        ctx2.drawImage(video, 0, 0, c.width, c.height);
                        const img = ctx2.getImageData(0, 0, c.width, c.height);
                        const b = calculateBrightness(img);
                        if (b < 35)
                            setResult("Слишком темно, улучшите освещение");
                    }
                }, 1000);
                setError(null);
                scanLoop();
            }
            catch (err) {
                console.error("Ошибка запуска камеры/WASM:", err);
                setError("Ошибка доступа к камере или инициализации WASM. Проверьте разрешения.");
                setIsScanning(false);
                stopAll();
            }
        });
        if (isScanning)
            start();
        return () => {
            if (brightnessTimer)
                window.clearInterval(brightnessTimer);
            stopAll();
        };
    }, [isScanning, facingMode]);
    const hasValidData = result !== "No result" &&
        result !== "QR-код не найден" &&
        result !== "Слишком темно, улучшите освещение" &&
        result !== "QR-код не содержит данные авторизации";
    // Диалог подтверждения для несовпадающего URL
    if (showUrlMismatch && qrData) {
        return (_jsxs("div", Object.assign({ style: { maxWidth: "500px", margin: "0 auto", padding: "20px" } }, { children: [_jsx("h2", Object.assign({ style: { color: "white" } }, { children: "QR-\u043A\u043E\u0434 \u0441 \u0434\u0440\u0443\u0433\u043E\u0433\u043E \u0441\u0435\u0440\u0432\u0435\u0440\u0430" })), _jsxs("div", Object.assign({ style: { color: "#ccc", marginBottom: "20px" } }, { children: [_jsxs("p", Object.assign({ style: { marginBottom: "10px" } }, { children: [_jsx("strong", { children: "\u0421\u0435\u0440\u0432\u0435\u0440 \u0432 QR:" }), " ", qrData.url] })), _jsxs("p", { children: [_jsx("strong", { children: "\u0422\u0435\u043A\u0443\u0449\u0438\u0439 \u0441\u0435\u0440\u0432\u0435\u0440:" }), " ", window.location.origin] })] })), _jsx("p", Object.assign({ style: { color: "white", fontSize: "16px", marginBottom: "20px" } }, { children: "\u041F\u0440\u043E\u0434\u043E\u043B\u0436\u0438\u0442\u044C \u0430\u0432\u0442\u043E\u0440\u0438\u0437\u0430\u0446\u0438\u044E?" })), _jsxs("div", Object.assign({ style: { display: "flex", gap: "10px" } }, { children: [_jsx(Button, Object.assign({ variant: "contained", color: "primary", onClick: () => handleAuthCodeApply(qrData.code) }, { children: "\u0414\u0430, \u0432\u043E\u0439\u0442\u0438" })), _jsx(Button, Object.assign({ variant: "outlined", color: "secondary", onClick: clearResult }, { children: "\u041E\u0442\u043C\u0435\u043D\u0430" }))] }))] })));
    }
    // Экран успеха
    if (showSuccess) {
        return (_jsxs("div", Object.assign({ style: { maxWidth: "400px", margin: "0 auto", padding: "20px" } }, { children: [_jsx("h2", Object.assign({ style: { color: "white" } }, { children: "\u0410\u0432\u0442\u043E\u0440\u0438\u0437\u0430\u0446\u0438\u044F \u0432\u044B\u043F\u043E\u043B\u043D\u0435\u043D\u0430" })), _jsx("p", Object.assign({ style: { color: "#ccc" } }, { children: "\u0412\u0445\u043E\u0434 \u0432 \u0441\u0438\u0441\u0442\u0435\u043C\u0443..." })), _jsx("div", Object.assign({ style: { display: "flex", gap: "10px", marginTop: "15px" } }, { children: _jsx(Button, Object.assign({ variant: "outlined", color: "secondary", onClick: onClose }, { children: "\u0417\u0430\u043A\u0440\u044B\u0442\u044C" })) }))] })));
    }
    // Экран сканирования
    return (_jsxs("div", Object.assign({ style: { maxWidth: "400px", margin: "0 auto", padding: "20px" } }, { children: [_jsx("h2", Object.assign({ style: { color: "white" } }, { children: "\u0421\u043A\u0430\u043D\u0435\u0440 QR-\u043A\u043E\u0434\u0430 \u0430\u0432\u0442\u043E\u0440\u0438\u0437\u0430\u0446\u0438\u0438" })), error ? (_jsx("p", Object.assign({ style: { color: "red" } }, { children: error }))) : (_jsxs(_Fragment, { children: [_jsxs("div", Object.assign({ style: { position: "relative" } }, { children: [_jsx("video", { ref: videoRef, playsInline: true, muted: true, style: {
                                    width: "100%",
                                    border: "2px solid #333",
                                    borderRadius: "8px",
                                } }), _jsx("div", { style: {
                                    position: "absolute",
                                    top: "50%",
                                    left: "50%",
                                    transform: "translate(-50%, -50%)",
                                    width: "60%",
                                    height: "60%",
                                    border: "3px solid #00ff00",
                                    borderRadius: "8px",
                                    pointerEvents: "none",
                                } })] })), _jsx("canvas", { ref: canvasRef, style: { display: "none" } }), _jsx("p", Object.assign({ style: { color: "white", fontSize: "15px", marginTop: "15px" } }, { children: "\u041E\u0442\u0441\u043A\u0430\u043D\u0438\u0440\u0443\u0439\u0442\u0435 QR-\u043A\u043E\u0434 \u0441 \u043A\u043E\u0434\u043E\u043C \u0430\u0432\u0442\u043E\u0440\u0438\u0437\u0430\u0446\u0438\u0438" })), result !== "No result" && result !== "Слишком темно, улучшите освещение" && (_jsx("p", Object.assign({ style: { color: "#ffa726", fontSize: "14px" } }, { children: result }))), _jsxs("div", Object.assign({ style: { display: "flex", gap: "10px", marginTop: "15px" } }, { children: [hasValidData && (_jsx(Button, Object.assign({ variant: "contained", color: "primary", onClick: clearResult }, { children: "\u041E\u0447\u0438\u0441\u0442\u0438\u0442\u044C \u0440\u0435\u0437\u0443\u043B\u044C\u0442\u0430\u0442" }))), _jsx(Button, Object.assign({ variant: "outlined", color: "secondary", onClick: toggleCamera }, { children: "\u041F\u0435\u0440\u0435\u043A\u043B\u044E\u0447\u0438\u0442\u044C \u043A\u0430\u043C\u0435\u0440\u0443" })), _jsx(Button, Object.assign({ variant: "outlined", color: "error", onClick: onClose }, { children: "\u041E\u0442\u043C\u0435\u043D\u0430" }))] }))] }))] })));
};
export default AuthQrCodeReader;
